import React, { lazy } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import './App.css';
import Maintenance from './pages/Maintenance';
import config from './config';
import JFLayout from './layouts/JFLayout/JFLayout';
import { useUserContext } from './context/UserContext';
import { useQueryClient } from '@tanstack/react-query';
import Loader from './components/Loader';
import accountsPageLoader from './pages/Accounts/accounts-page-loader';
import projectsPageLoader from './pages/ProjectsV2/projects-page-loader';
import {
  myTasksPageLoader,
  queuesPageLoader,
  taskPageLoader,
} from './pages/TasksPage/tasks-page-loader';
import { TimelinePageWrap } from './component-lib/Timeline';
import EmptyPage from './pages/EmptyPage';
import {
  flowsPageLoader,
  editFlowPageLoader,
} from './pages/Settings/Flows/single/flows-page-loader';
import {
  flowGroupsPageLoader,
  editFlowGroupPageLoader,
} from './pages/Settings/Flows/group/flow-group-page-loaders';
import {
  editPlaybookPageLoader,
  playbooksPageLoader,
  playbookTaskGroupPageLoader,
  playbookTaskPageLoader,
} from './pages/Settings/Playbooks/playbook-page-loaders';

import {
  loadExistingChatMessage,
  projectDetailPageLoader,
  projectTaskGroupPageLoader,
  redirectLoader,
} from './pages/ProjectDetail/project-detail-page-loaders';
import { defaultRoutes } from './routes/default-routes';
import { crmProjectPageLoader } from './pages/CrmProject/crm-project-page-loader';
import loginPageLoader from './pages/Login/login-page-loader';
import { dealImporterPageLoader } from './pages/DealImporter/deal-importer-page-loader';
import { timelineLoader } from './component-lib/Timeline/timeline-loader';
import { myTasksDashboardLoader } from './pages/MyTasks/Dashboard/my-tasks-dashboard-loader';

const Home = lazy(() => import('./pages/Home/Homepage'));
const Login = lazy(() => import('./pages/Login/Login'));
const Profile = lazy(() => import('./pages/Profile'));
// const CrmTable = lazy(() => import('./components/Crm/CrmTable'));
const DealImporterPage = lazy(() => import('./pages/DealImporter'));
const Workload = lazy(() => import('./pages/Workload'));
const BoardWorkload = lazy(() => import('./pages/BoardWorkload'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const InstancePage = lazy(() => import('./pages/InstancePage'));
const SuperUserPage = lazy(() => import('./pages/SuperUserPage'));
const CreateProject = lazy(() => import('./pages/CreateProject'));
const CrmProject = lazy(() => import('./pages/CrmProject'));
const Admin = lazy(() => import('./pages/Adminpage/Adminpage'));
const AccountsPage = lazy(() => import('./pages/Accounts'));
const ProjectsPageV2 = lazy(() => import('./pages/ProjectsV2'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const TaskEditor = lazy(() => import('./pages/TasksPage/TaskEditor'));
const EditPlaybookPage = lazy(() => import('./pages/Settings/Playbooks/EditPlaybookPage'));
const PlaybookContents = lazy(() => import('./pages/Settings/Playbooks/PlaybookContents'));
const PlaybookStatuses = lazy(() => import('./pages/Settings/Playbooks/PlaybookStatuses'));
const PlaybookFlows = lazy(() => import('./pages/Settings/Playbooks/PlaybookFlows'));
const PlaybookTask = lazy(() => import('./pages/Settings/Playbooks/PlaybookTask'));
const PlaybookTaskGroup = lazy(() => import('./pages/Settings/Playbooks/PlaybookTaskGroup'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const EditFlowPage = lazy(() => import('./pages/Settings/Flows/single/EditFlowPage'));
const EditFlowGroupPage = lazy(() => import('./pages/Settings/Flows/group/EditFlowGroupPage'));
const FlowsPage = lazy(() => import('./pages/Settings/Flows'));
const PlaybooksPage = lazy(() => import('./pages/Settings/Playbooks'));
const DocumentLibraryPage = lazy(() => import('./pages/Settings/DocumentLibrary'));
const ContentLibraryPage = lazy(() => import('./pages/Settings/ContentLibrary'));
const UsersAndAccessPage = lazy(() => import('./pages/Settings/UsersAndAccess'));
const CompaniesAndContactsPage = lazy(() => import('./pages/Settings/CompaniesAndContacts'));
const AdvancedSettingsPage = lazy(() => import('./pages/Settings/AdvancedSettings'));
const FlowGroupsPage = lazy(() => import('./pages/Settings/Flows/group/FlowGroups'));
const FlowListPage = lazy(() => import('./pages/Settings/Flows/single/FlowList'));
const PlaybookDetails = lazy(() => import('./pages/Settings/Playbooks/PlaybookDetails'));
const ProjectTaskGroupEditor = lazy(() => import('./pages/ProjectDetail/tasks/TaskGroupEditor'));
const MyTasksDashboard = lazy(() => import('./pages/MyTasks/Dashboard'));
const MyTasks = lazy(() => import('./pages/MyTasks'));
const DetailsTab = lazy(() => import('./pages/ProjectDetail/tabs/Details'));
const Assistant = lazy(() => import('./pages/ProjectDetail/tabs/Assistant'));
const Main = lazy(() => import('./pages/ProjectDetail/tabs/Main'));
const DocumentsTab = lazy(() => import('./pages/ProjectDetail/tabs/Documents'));
const MaapContentTab = lazy(() => import('./pages/ProjectDetail/tabs/MaapContent'));
const MaapAnalytics = lazy(() => import('./pages/ProjectDetail/tabs/map-analytics/MaapAnalytics'));
const MessagesTab = lazy(() => import('./pages/ProjectDetail/tabs/Messages'));
const MeddpiccTab = lazy(() => import('./pages/ProjectDetail/tabs/Meddpicc'));
const WarningsTab = lazy(() => import('./pages/ProjectDetail/tabs/Warnings'));
const ChatDetails = lazy(() => import('./pages/ProjectDetail/tabs/assistant/ChatDetails'));
const ActionSelector = lazy(() => import('./pages/ProjectDetail/tabs/assistant/ActionSelector'));

export default function AppRoutes() {
  const queryClient = useQueryClient();
  const { hasToken, hasAdminToken, user } = useUserContext();
  const environment = config.REACT_APP_ENV;

  if (environment === 'maintenance') {
    return <Maintenance />;
  }

  // Routes that are only available when a user is not authenticated
  const loggedOutRoutes = [
    { path: '/login', element: <Login />, loader: loginPageLoader(queryClient) },
  ];

  // Routes availble when the token is an admin token
  const adminRoutes = [
    {
      path: '/admin',
      element: <Admin />,
    },
    {
      path: '/createinstance',
      element: <InstancePage />,
    },
    {
      path: '/superuser',
      element: <SuperUserPage />,
    },
  ];

  // Routes that are only accessible for authenticated users
  const appRoutes = [
    {
      path: '/timeline',
      element: (
        <JFLayout tabpanel={true}>
          <TimelinePageWrap />
        </JFLayout>
      ),
      loader: timelineLoader(queryClient),
    },
    {
      path: '/empty-test',
      element: (
        <JFLayout>
          <EmptyPage />
        </JFLayout>
      ),
    },
    {
      path: '/',
      element: (
        <JFLayout>
          <Home />
        </JFLayout>
      ),
      children: [
        {
          path: 'p/:projectId/t/:taskId',
          element: <TaskEditor onTaskEditorClose="GO_BACK" />,
          loader: taskPageLoader(queryClient),
        },
      ],
    },
    {
      path: '/accounts',
      element: <JFLayout />,
      children: [
        {
          index: true,
          element: (
            <React.Suspense fallback={<Loader />}>
              <AccountsPage />
            </React.Suspense>
          ),
          loader: accountsPageLoader(queryClient),
        },
      ],
    },
    {
      path: '/board',
      element: (
        <JFLayout>
          <ProjectDetail />
        </JFLayout>
      ),
      loader: redirectLoader,
    },
    // {
    //   path: '/legal',
    //   element: (
    //     <JFLayout>
    //       <Legal />
    //     </JFLayout>
    //   ),
    // },
    {
      path: '/settings',
      element: <JFLayout />,
      children: [
        {
          path: 'flows',
          Component: FlowsPage,
          children: [
            {
              path: '',
              Component: FlowListPage,
              children: [
                {
                  path: ':flowId',
                  element: <EditFlowPage />,
                  loader: editFlowPageLoader(queryClient),
                },
              ],
              loader: flowsPageLoader(queryClient),
            },
            {
              path: 'groups',
              Component: FlowGroupsPage,
              children: [
                {
                  path: ':flowGroupId',
                  element: <EditFlowGroupPage />,
                  loader: editFlowGroupPageLoader(queryClient),
                },
              ],
              loader: flowGroupsPageLoader(queryClient),
            },
          ],
        },
        {
          path: 'playbooks',
          Component: PlaybooksPage,
          loader: playbooksPageLoader(queryClient),
        },
        {
          path: 'playbooks/:playbookId',
          Component: EditPlaybookPage,
          loader: editPlaybookPageLoader(queryClient),
          children: [
            {
              path: '',
              Component: PlaybookFlows,
              children: [
                {
                  path: 'flows/:flowId',
                  Component: PlaybookTask,
                  loader: playbookTaskPageLoader(queryClient),
                },
                {
                  path: 'flow-groups/:flowGroupId',
                  Component: PlaybookTaskGroup,
                  loader: playbookTaskGroupPageLoader(queryClient),
                },
              ],
            },
            {
              path: 'details',
              Component: PlaybookDetails,
            },
            {
              path: 'contents',
              Component: PlaybookContents,
            },
            {
              path: 'statuses',
              Component: PlaybookStatuses,
            },
          ],
        },
        {
          path: 'document-library',
          Component: DocumentLibraryPage,
        },
        {
          path: 'content-library',
          Component: ContentLibraryPage,
        },
        {
          path: 'users-access',
          Component: UsersAndAccessPage,
        },
        {
          path: 'companies-contacts',
          Component: CompaniesAndContactsPage,
        },
        {
          path: 'advanced',
          Component: AdvancedSettingsPage,
        },
      ],
    },
    {
      path: '/profile',
      element: (
        <JFLayout tabpanel>
          <Profile />
        </JFLayout>
      ),
    },
    {
      path: '/projects',
      element: <JFLayout tabpanel={true} />,
      children: [
        {
          path: '',
          element: (
            <React.Suspense fallback={<Loader />}>
              <ProjectsPageV2 />
            </React.Suspense>
          ),
          loader: projectsPageLoader(queryClient),
        },
        {
          path: ':projectId',
          id: 'projectDetails',
          Component: ProjectDetail,
          loader: projectDetailPageLoader(queryClient),
          children: [
            {
              path: '',
              Component: Main,
            },
            {
              path: 'timeline',
              Component: Main,
              children: [
                {
                  path: 'tasks/:taskId',
                  Component: TaskEditor,
                  loader: taskPageLoader(queryClient),
                },
                {
                  path: 'task-groups/:taskGroupId',
                  Component: ProjectTaskGroupEditor,
                  loader: projectTaskGroupPageLoader(queryClient),
                },
              ],
            },
            {
              path: 'details',
              Component: DetailsTab,
            },
            {
              path: 'documents',
              Component: DocumentsTab,
            },
            {
              path: 'maap-content',
              Component: MaapContentTab,
            },
            {
              path: 'maap-analytics',
              Component: MaapAnalytics,
            },
            {
              path: 'messages',
              Component: MessagesTab,
            },
            {
              path: 'meddpicc',
              Component: MeddpiccTab,
            },
            {
              path: 'assistant',
              Component: Assistant,
              children: [
                {
                  path: 'chats/:chatId/messages/:messageId',
                  Component: ChatDetails,
                  loader: loadExistingChatMessage(queryClient),
                },
                {
                  index: true,
                  Component: ActionSelector,
                },
              ],
            },
            {
              path: 'warnings',
              Component: WarningsTab,
            },
            {
              path: 'tasks/:taskId',
              Component: TaskEditor,
              loader: taskPageLoader(queryClient),
            },
            {
              path: 'task-groups/:taskGroupId',
              Component: ProjectTaskGroupEditor,
              loader: projectTaskGroupPageLoader(queryClient),
            },
          ],
        },
      ],
    },
    {
      path: '/crm_deals',
      element: (
        <JFLayout>
          <DealImporterPage />
        </JFLayout>
      ),
      loader: dealImporterPageLoader(queryClient),
    },
    {
      path: '/create_project',
      element: (
        <JFLayout>
          <CreateProject />
        </JFLayout>
      ),
    },
    {
      path: '/crm_project',
      element: (
        <JFLayout>
          <CrmProject />
        </JFLayout>
      ),
      loader: crmProjectPageLoader(queryClient),
    },
    {
      path: '/my_tasks',
      element: <JFLayout />,
      children: [
        {
          Component: MyTasks,
          children: [
            {
              path: '',
              element: (
                <React.Suspense fallback={<Loader />}>
                  <MyTasksDashboard />
                </React.Suspense>
              ),
              loader: myTasksDashboardLoader(queryClient, user),
              children: [
                {
                  path: 'p/:projectId/t/:taskId',
                  element: <TaskEditor onTaskEditorClose="GO_BACK" />,
                  loader: taskPageLoader(queryClient),
                },
              ],
            },
            {
              path: 'list-view',
              element: (
                <React.Suspense fallback={<Loader />}>
                  <TasksPage forCurrentUser />
                </React.Suspense>
              ),
              loader: myTasksPageLoader(queryClient, user),
              children: [
                {
                  path: 'p/:projectId/t/:taskId',
                  element: <TaskEditor onTaskEditorClose="GO_BACK" />,
                  loader: taskPageLoader(queryClient),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: '/queues',
      element: <JFLayout />,
      children: [
        {
          path: '',
          element: (
            <React.Suspense fallback={<Loader />}>
              <TasksPage />
            </React.Suspense>
          ),
          loader: queuesPageLoader(queryClient),
          children: [
            {
              path: 'p/:projectId/t/:taskId',
              element: <TaskEditor onTaskEditorClose="GO_BACK" />,
              loader: taskPageLoader(queryClient),
            },
          ],
        },
      ],
    },
    {
      path: '/workload_priorities',
      element: (
        <JFLayout>
          <Workload />
        </JFLayout>
      ),
    },
    {
      path: '/board_priorities',
      element: (
        <JFLayout>
          <BoardWorkload />
        </JFLayout>
      ),
    },
    {
      path: '/analytics',
      element: (
        <JFLayout>
          <AnalyticsPage />
        </JFLayout>
      ),
    },
  ];

  const routes = [...defaultRoutes(queryClient)];

  if (hasToken) {
    routes.push(...appRoutes);
  }

  if (hasAdminToken) {
    routes.push(...adminRoutes);
  }

  if (!hasToken) {
    routes.push(...loggedOutRoutes);
  }

  // TODO where to add <React.Suspense fallback={<Loader fullscreen />}>
  const router = createBrowserRouter(routes, {
    basename: `${process.env.PR_NUMBER ? '/pr-' + process.env.PR_NUMBER : ''}`,
  });

  return (
    <React.Suspense fallback={<Loader fullscreen />}>
      <RouterProvider router={router} />
    </React.Suspense>
  );
}
